import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@giphy/react-components';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Tooltip,
  TooltipProps,
  useDebounce,
} from 'material-hu';
import gifIcon from 'src/assets/gifUpload.svg';
import SearchBar from 'src/components/search/SearchBar';
import { getTrendingGifs, searchGifs } from 'src/services/attachments';
import PoweredByGiphyLogo from 'src/assets/poweredByGiphy.png';

type Props = Pick<TooltipProps, 'placement'> & {
  onGifSelection?: (gif) => void;
  title: string;
  iconColor: string;
  disabled?: boolean;
};

export const FormGif: FC<Props> = props => {
  const { onGifSelection, title, placement, iconColor, disabled } = props;

  const [query, setQuery] = useState<string>('');
  const [openModal, setOpenModal] = useState(false);
  const [isGifSelectable, setIsGifSelectable] = useState(false);

  const { t } = useTranslation(['ATTACHMENT']);

  const searchTerm = useDebounce(query);

  const fetchGifs = (offset: number) =>
    searchTerm
      ? searchGifs(searchTerm, { offset, limit: 10, rating: 'pg-13' })
      : getTrendingGifs({ offset, limit: 10, rating: 'pg-13' });

  useEffect(() => {
    let timer;
    if (!isGifSelectable) {
      timer = setTimeout(() => {
        setIsGifSelectable(true);
      }, 500);
    }
    return () => clearTimeout(timer);
  }, [isGifSelectable]);

  const handleOpen = () => setOpenModal(true);

  const handleClose = () => {
    setIsGifSelectable(false);
    setQuery('');
    setOpenModal(false);
  };

  const setNewGif = (gif, e) => {
    e.preventDefault();
    if (!isGifSelectable) return;
    setIsGifSelectable(false);
    handleClose();
    onGifSelection(gif);
  };

  const handleChange = value => setQuery(value);

  return (
    <>
      <Dialog
        open={openModal}
        onClose={handleClose}
      >
        <DialogTitle>
          <Typography
            variant="h6"
            sx={{ mb: 1 }}
          >
            {t('SELECT_GIF')}
          </Typography>
          <SearchBar
            placeholder={t('SEARCH_GIF')}
            inputProps={{ maxlength: 50 }}
            query={query}
            onChange={handleChange}
          />
        </DialogTitle>
        <DialogContent sx={{ height: '300px', minWidth: '490px' }}>
          <img
            src={PoweredByGiphyLogo}
            style={{ marginBottom: '10px' }}
          />
          <Grid
            hideAttribution
            noResultsMessage={
              <Typography>
                {t('GIF_NOT_FOUND', { search: searchTerm })}
              </Typography>
            }
            width={490}
            onGifClick={setNewGif}
            columns={3}
            key={searchTerm}
            fetchGifs={fetchGifs}
          />
        </DialogContent>
      </Dialog>
      <Tooltip
        title={title}
        placement={placement}
      >
        <IconButton
          aria-label={t('ATTACH_GIF')}
          disabled={disabled}
          edge="end"
          onClick={handleOpen}
          sx={{ color: iconColor }}
          size="large"
        >
          <img
            src={gifIcon}
            alt=""
            style={{
              filter: disabled ? 'opacity(50%)' : 'none',
              width: '20px',
              height: '20px',
            }}
          />
        </IconButton>
      </Tooltip>
    </>
  );
};

export default FormGif;
